import { useState } from "react"
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import {
  useEmailStats,
  type EmailTemplateStat,
  type SesReputationStats,
} from "@/hooks/useEmailStats"

const sesDashboardUrl = (region: string) =>
  `https://${region}.console.aws.amazon.com/ses/home?region=${region}#/account`

const pct = (rate: number | null) => (rate == null ? "—" : `${(rate * 100).toFixed(2)}%`)
const count = (n: number | null) => (n == null ? "—" : n.toLocaleString())

const WINDOWS: Array<{ label: string; days: number }> = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
]

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string
  value: string
  tone?: "good" | "bad" | "neutral"
}) {
  const valueColor =
    tone === "good" ? "text-green-400" : tone === "bad" ? "text-rose-400" : "text-primary"
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="text-xs text-secondary">{label}</div>
      <div className={`text-xl font-semibold ${valueColor}`}>{value}</div>
    </div>
  )
}

// A count cell that goes rose when it's a "didn't land" status with a non-zero
// count, so a problem template jumps out of the table.
function Count({ n, bad = false }: { n: number; bad?: boolean }) {
  if (n === 0) return <span className="text-secondary/50">0</span>
  return <span className={bad ? "font-medium text-rose-400" : "text-primary"}>{n}</span>
}

function deliveredPct(row: EmailTemplateStat): string {
  return row.total > 0 ? pct(row.delivered / row.total) : "—"
}

function ReputationPanel({
  reputation,
  windowDays,
}: {
  reputation: SesReputationStats | undefined
  windowDays: number
}) {
  const elevated =
    !!reputation &&
    ((reputation.bounceRate.max ?? 0) >= reputation.thresholds.bounceRateReview ||
      (reputation.complaintRate.max ?? 0) >= reputation.thresholds.complaintRateReview)

  return (
    <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-primary">SES Account Reputation</h2>
          <p className="text-sm text-secondary">
            CloudWatch AWS/SES reputation metrics for the account in the selected window.
          </p>
        </div>
        <a
          href={sesDashboardUrl(reputation?.region ?? "us-east-2")}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          SES dashboard
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </div>

      {!reputation ? (
        <p className="text-sm text-secondary">Loading…</p>
      ) : !reputation.configured ? (
        <div className="rounded-md border border-border bg-background/40 p-3 text-sm text-secondary">
          SES reputation metrics are not configured for this backend. Add CloudWatch read
          credentials to the Worker to show AWS account-level rates here.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label="Bounce rate max"
              value={pct(reputation.bounceRate.max)}
              tone={
                (reputation.bounceRate.max ?? 0) >= reputation.thresholds.bounceRateReview
                  ? "bad"
                  : "neutral"
              }
            />
            <Stat
              label="Complaint rate max"
              value={pct(reputation.complaintRate.max)}
              tone={
                (reputation.complaintRate.max ?? 0) >= reputation.thresholds.complaintRateReview
                  ? "bad"
                  : "neutral"
              }
            />
            <Stat label="SES sends" value={count(reputation.sends)} />
            <Stat label="SES deliveries" value={count(reputation.deliveries)} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="SES bounces" value={count(reputation.bounces)} />
            <Stat label="SES complaints" value={count(reputation.complaints)} />
            <Stat label="SES rejects" value={count(reputation.rejects)} />
          </div>

          {elevated && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              <span>
                SES account reputation is at or above AWS review guidance: 5% bounce or 0.1%
                complaint. These CloudWatch rates are fractions from AWS, formatted here as
                percentages.
              </span>
            </div>
          )}

          {reputation.error && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
              CloudWatch returned no usable SES reputation data: {reputation.error}.
            </div>
          )}

          <p className="text-sm text-secondary">
            Region <span className="font-medium text-primary">{reputation.region}</span>, last{" "}
            {windowDays} days, {reputation.periodSeconds / 60}-minute CloudWatch periods. Empty
            metric panes usually mean CloudWatch has not emitted that SES metric in the window.
          </p>
        </>
      )}
    </section>
  )
}

export function EmailStats() {
  const [windowDays, setWindowDays] = useState(30)
  const { data, isLoading } = useEmailStats(windowDays)

  // Local transactional telemetry still gets its own warning: it can catch a
  // broken template before the account-level SES reputation window reacts.
  const elevated = !!data && (data.bounceRate >= 0.05 || data.complaintRate >= 0.001)

  return (
    <div className="space-y-6">
      {/* Window selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-secondary">Window</span>
        <div className="inline-flex rounded-md border border-border bg-background p-1">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setWindowDays(w.days)}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                windowDays === w.days
                  ? "bg-primary text-contrast"
                  : "text-secondary hover:text-primary"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <ReputationPanel reputation={data?.sesReputation} windowDays={windowDays} />

      {/* Headline rollup */}
      <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
        <h2 className="text-lg font-semibold text-primary">
          Playside Transactional Delivery (last {windowDays} days)
        </h2>

        {isLoading ? (
          <p className="text-sm text-secondary">Loading…</p>
        ) : !data || data.total === 0 ? (
          <p className="text-sm text-secondary">
            No transactional sends recorded in the last {windowDays} days.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Sends" value={data.total.toLocaleString()} />
              <Stat label="Delivered" value={pct(data.deliveredRate)} tone="good" />
              <Stat
                label="Bounced"
                value={pct(data.bounceRate)}
                tone={data.bounceRate >= 0.05 ? "bad" : "neutral"}
              />
              <Stat
                label="Complained"
                value={pct(data.complaintRate)}
                tone={data.complaintRate >= 0.001 ? "bad" : "neutral"}
              />
            </div>

            {elevated && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                <span>
                  Logged transactional bounce/complaint rates are elevated. Compare this with the
                  account-level SES reputation panel before deciding whether it is an AWS reputation
                  issue or a Playside template/list issue.
                </span>
              </div>
            )}
          </>
        )}

        <p className="text-sm text-secondary">
          These are{" "}
          <span className="font-medium text-primary">
            Playside&apos;s logged transactional sends
          </span>{" "}
          (from{" "}
          <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">
            email_events
          </code>
          ), which can differ from account-level SES reputation because AWS calculates reputation
          from representative account volume.
        </p>
      </section>

      {/* Per-template breakdown */}
      <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-primary">By template</h2>
          <p className="text-sm text-secondary">
            Which templates drive the volume — and the bounces. Busiest first.
          </p>
        </div>

        {isLoading ? (
          <p className="text-sm text-secondary">Loading…</p>
        ) : !data || data.byTemplate.length === 0 ? (
          <p className="text-sm text-secondary">No sends to break down in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-secondary">
                  <th className="py-2 pr-4 font-medium">Template</th>
                  <th className="py-2 pr-4 text-right font-medium">Sends</th>
                  <th className="py-2 pr-4 text-right font-medium">Delivered</th>
                  <th className="py-2 pr-4 text-right font-medium">Delivered %</th>
                  <th className="py-2 pr-4 text-right font-medium">Bounced</th>
                  <th className="py-2 pr-4 text-right font-medium">Complained</th>
                  <th className="py-2 text-right font-medium">Failed</th>
                </tr>
              </thead>
              <tbody>
                {data.byTemplate.map((row) => (
                  <tr key={row.type} className="border-b border-border/50">
                    <td className="py-2 pr-4 font-mono text-xs text-primary">{row.type}</td>
                    <td className="py-2 pr-4 text-right text-primary">
                      {row.total.toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Count n={row.delivered} />
                    </td>
                    <td className="py-2 pr-4 text-right text-secondary">{deliveredPct(row)}</td>
                    <td className="py-2 pr-4 text-right">
                      <Count n={row.bounced} bad />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Count n={row.complained} bad />
                    </td>
                    <td className="py-2 text-right">
                      <Count n={row.failed} bad />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
